import { useTranslation } from 'next-i18next';
import React, { useContext, useEffect, useState, VFC } from 'react';

import { fetchTokenByID } from '@/apis/aniftyBackend/aniftyTokens/fetchTokenByID';
import { AniftyTokenDetailData } from '@/apis/aniftyBackend/aniftyTokens/interface';
import { buyFromPrimarySale } from '@/apis/blockchain/AniftySale';
import { marketplaceV1GraphClient } from '@/apis/theGraph';
import LoadingCard from '@/components/LoadingCard';
import { TransactionSucceededMessage } from '@/components/TransactionSucceededMessage';
import HIDDEN_CREATION_IDS from '@/constants/hiddenCreationIDs';
import NOT_ON_SALE_TOKEN_IDS from '@/constants/notOnSaleTokenIDs';
import CreationSaleCard from '@/containers/creations/CreationDetail/CreationSaleCard';
import { Web3Context } from '@/contexts/Web3Provider/Web3Provider';
import { captureException } from '@/utils/Sentry';

import styles from './V1CreationSale.module.scss';
import routes from '@/config/routes';
import { useRouter } from 'next/router';
import redirectTargets from '@/config/redirectTargets';

type Props = {
  token: AniftyTokenDetailData;
};

/**
 * Token Info for Marketplace V1
 */

const V1CreationSale: VFC<Props> = ({ token }) => {
  const { t } = useTranslation();

  const web3Context = useContext(Web3Context);
  const router = useRouter();

  const [NFTPurchaseAmt, setNFTPurchaseAmt] = useState('1');
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastTransactionHash, setLastTransactionHash] =
    useState<null | string>(null);

  const [tokenDataClient, setTokenDataClient] =
    useState<AniftyTokenDetailData | null>({
      ...token,
      ownersInfo: undefined,
      totalTokenAmount: undefined,
      availableTokenAmount: undefined,
      creatorInfo: {
        ...token.creatorInfo,
        primarySellingTokenInfo: undefined,
      },
      currentETHPrice: undefined,
    });

  const fetchTokenData = async () => {
    const result = await fetchTokenByID(tokenDataClient.aniftyTokenID);
    setTokenDataClient(result);
  };

  // price and supply is initially obtained from backend
  // then replaced with the latest one from the Graph
  const [price, setPrice] = useState<string>(
    token.creatorInfo.primarySellingTokenInfo.price
  );
  const [supply, setSupply] = useState<number>(
    tokenDataClient.availableTokenAmount
  );

  useEffect(() => {
    if (!tokenDataClient) {
      return;
    }

    // get primary sale info from v1 graph to update latest price & supply
    marketplaceV1GraphClient
      .getPrimarySalesInfoByTokenID(tokenDataClient.aniftyTokenID)
      .then(({ primarySales }) => {
        if (primarySales && primarySales.length) {
          const primarySale = primarySales
            .map((ps) => ({
              ...ps,
              creationTimestampNum: Number(ps.creationTimestamp),
            }))
            // get the latest one
            .sort((a, b) => a.creationTimestampNum - b.creationTimestampNum)[0];

          // update price
          if (primarySale.price) {
            setPrice(primarySale.price);
          }

          // update supply
          if (primarySale.tokenAmountUnsold) {
            setSupply(Number(primarySale.tokenAmountUnsold));
          }
        }
      });
  }, [tokenDataClient]);

  useEffect(() => {
    fetchTokenData();
  }, []);

  useEffect(() => {
    // reload token availability on transaction completed
    fetchTokenData();
  }, [lastTransactionHash]);

  const handleBuyButtonClick = async () => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    if (!web3Context.web3.AniftyMarketplaceV2Ipfs) {
      // metamask is not available
      await router.push(`${routes.connectWallet}?${new URLSearchParams(redirectTargets.creationDetail(token.creationID))}`);
    }

    try {
      const response = await buyFromPrimarySale(
        web3Context.web3.AniftySaleContract,
        tokenDataClient.creatorInfo.primarySellingTokenInfo.saleListingID,
        NFTPurchaseAmt
      );

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (response?.transactionHash) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setLastTransactionHash(response.transactionHash);
      }
    } catch (err) {
      if (err.code === 'INSUFFICIENT_FUNDS') {
        setErrorMessage(t('creationPage.insufficientFundsError'));
      } else if (err.code === "UNSUPPORTED_OPERATION") {
        // metamask is available, but wallet is not connected
        await router.push(`${routes.connectWallet}?${new URLSearchParams(redirectTargets.creationDetail(token.creationID))}`);
      } else {
        setErrorMessage(t('creationPage.transactionError'));
      }
      captureException(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {typeof tokenDataClient.totalTokenAmount === 'undefined' ? (
        <LoadingCard message="Loading Sale Data" />
      ) : (
        <CreationSaleCard
          totalEditions={tokenDataClient.totalTokenAmount}
          availableTokenAmount={supply}
          ethSalePrice={price}
          purchaseAmount={parseInt(NFTPurchaseAmt)}
          onPurchaseAmountChange={(num: number) =>
            setNFTPurchaseAmt(num.toString())
          }
          isCancelled={false}
          isNotOnSale={
            NOT_ON_SALE_TOKEN_IDS.includes(tokenDataClient.aniftyTokenID) ||
            HIDDEN_CREATION_IDS.includes(tokenDataClient.creationID)
          }
          isNotReleased={false}
          isProcessing={isProcessing}
          onBuyButtonClick={handleBuyButtonClick}
        />
      )}

      <div className={styles.buyButtonContainer}>
        {errorMessage && (
          <div className={styles.errorMessage}>{errorMessage}</div>
        )}
      </div>

      {lastTransactionHash && (
        <TransactionSucceededMessage transactionHash={lastTransactionHash} />
      )}
    </>
  );
};

export default V1CreationSale;
